import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import WarningIcon from '@mui/icons-material/Warning';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ListEmptyState from './ListEmptyState';
import { queryPackingListItems, PackingListItem, queryItemTypesWithLocations } from '../schema';
import { useMemo, useState } from 'react';
import { db } from '../instant';
import PackingListItemRow from './PackingListItemRow';
import AddPackingListItemDialog from './AddPackingListItemDialog';
import CopyPackingListDialog from './CopyPackingListDialog';
import RecordInventoryChangeDialog from './RecordInventoryChangeDialog';
import { calculateAvailability } from '../utils/inventory';
import { PACKING_LIST_LOCK_MESSAGE, useTripLock } from '../hooks/useTripLock';
import { ItemType, queryItemTypes } from '../schema';

interface Props {
    eventId: string;
    onAddItem?: () => void;
    onCopyFromEvent?: () => void;
}

export default function EventInventoryPackingList({ eventId, onAddItem: _onAddItem, onCopyFromEvent: _onCopyFromEvent }: Props) {
    const { lockedEventIds } = useTripLock();
    const packingListLocked = lockedEventIds.has(eventId);
    const { data, isLoading, error } = queryPackingListItems(eventId);
    const { data: itemTypesData } = queryItemTypesWithLocations(); // Query all itemTypes with locations for availability
    const { data: allItemTypesData } = queryItemTypes();
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
    const [recordDialogOpen, setRecordDialogOpen] = useState(false);
    const [recordDialogMode, setRecordDialogMode] = useState<'lostOrBroken'>('lostOrBroken');
    const [itemForRecord, setItemForRecord] = useState<ItemType | null>(null);

    const event = data?.events?.[0];
    const packingListItems = useMemo(() => (event?.packingListItems || []) as PackingListItem[], [event?.packingListItems]);

    // Extract all itemLocations from itemTypes for availability calculation
    const allItemLocations = useMemo(() => {
        const locations: Array<{
            id: string;
            quantity: number;
            itemType?: { id: string } | null;
            event?: { id: string } | null;
        }> = [];

        const itemTypes = itemTypesData?.itemTypes || [];
        for (const itemType of itemTypes) {
            const itemLocations = (itemType as any).itemLocations || [];
            for (const location of itemLocations) {
                locations.push({
                    id: location.id,
                    quantity: location.quantity || 0,
                    itemType: { id: itemType.id },
                    event: location.event ? { id: location.event.id } : null,
                });
            }
        }
        return locations;
    }, [itemTypesData]);

    // Separate trailers from other items
    const { trailers, otherItems } = useMemo(() => {
        const trailersList: PackingListItem[] = [];
        const otherList: PackingListItem[] = [];

        for (const item of packingListItems) {
            const categoryName = item.itemType?.category?.name || '';
            if (categoryName === 'Trailers') {
                trailersList.push(item);
            } else {
                otherList.push(item);
            }
        }

        // Sort other items by category, then alphabetical by name
        otherList.sort((a, b) => {
            const catA = a.itemType?.category?.name ?? '';
            const catB = b.itemType?.category?.name ?? '';
            const cmp = catA.localeCompare(catB);
            if (cmp !== 0) return cmp;
            const nameA = a.itemType?.name ?? '';
            const nameB = b.itemType?.name ?? '';
            return nameA.localeCompare(nameB);
        });

        return { trailers: trailersList, otherItems: otherList };
    }, [packingListItems]);

    const trailerCount = trailers.reduce((sum, item) => sum + (item.quantity || 0), 0);

    const handleRecordLostOrBroken = (itemTypeId: string, _eventId: string) => {
        const itemType = allItemTypesData?.itemTypes?.find(it => it.id === itemTypeId);
        if (itemType) {
            setItemForRecord(itemType);
            setRecordDialogMode('lostOrBroken');
            setRecordDialogOpen(true);
        }
    };

    const handleRecordDialogClose = () => {
        setRecordDialogOpen(false);
        setItemForRecord(null);
    };

    // Calculate insufficient items summary
    const insufficientItems = useMemo(() => {
        const insufficient: Array<{ item: PackingListItem; shortage: number }> = [];
        for (const item of packingListItems) {
            if (!item.itemType) continue;
            const availability = calculateAvailability(item.itemType.id, item.quantity || 0, allItemLocations, eventId);
            if (availability.status === 'insufficient') {
                insufficient.push({ item, shortage: availability.shortage });
            }
        }
        return insufficient;
    }, [packingListItems, allItemLocations, eventId]);

    if (isLoading) {
        return (
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
                </Box>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
            </Stack>
        );
    }

    if (error) {
        return (
            <Typography variant="body2" color="error" sx={{ py: 2 }}>
                Error loading packing list: {error?.message || 'Unknown error'}
            </Typography>
        );
    }

    const lockTipProps = {
        title: packingListLocked ? PACKING_LIST_LOCK_MESSAGE : '',
        disableHoverListener: !packingListLocked,
    };

    return (
        <Box>
            {packingListLocked && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {PACKING_LIST_LOCK_MESSAGE}
                </Alert>
            )}
            {/* Section Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {packingListItems.length === 0 && (
                        <Tooltip {...lockTipProps}>
                            <span>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ContentCopyIcon />}
                                    disabled={packingListLocked}
                                    onClick={() => setCopyDialogOpen(true)}
                                >
                                    Copy from Event
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    <Tooltip {...lockTipProps}>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                disabled={packingListLocked}
                                onClick={() => setAddDialogOpen(true)}
                            >
                                Add Item
                            </Button>
                        </span>
                    </Tooltip>
                    {packingListItems.length > 0 && (
                        <Tooltip {...lockTipProps}>
                            <span>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteSweepIcon />}
                                    disabled={packingListLocked}
                                    onClick={() => setClearConfirmOpen(true)}
                                >
                                    Clear packing list
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)}>
                <DialogTitle>Clear packing list?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will remove all {packingListItems.length} item{packingListItems.length === 1 ? '' : 's'} from the packing list
                        for this event. This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={async () => {
                            try {
                                await db.transact(packingListItems.map(pi => db.tx.packingListItems[pi.id].delete()));
                                setClearConfirmOpen(false);
                            } catch (err) {
                                console.error('Error clearing packing list:', err);
                            }
                        }}
                    >
                        Clear packing list
                    </Button>
                </DialogActions>
            </Dialog>

            <AddPackingListItemDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                eventId={eventId}
                onItemAdded={() => {}}
            />

            <CopyPackingListDialog
                open={copyDialogOpen}
                onClose={() => setCopyDialogOpen(false)}
                eventId={eventId}
                onCopied={() => setCopyDialogOpen(false)}
            />

            {packingListItems.length === 0 ? (
                <ListEmptyState
                    icon={<ChecklistIcon sx={{ fontSize: 48 }} />}
                    message="Packing list is empty"
                    subtext={
                        packingListLocked
                            ? 'You cannot add items while a packing operation that includes this event is in progress.'
                            : 'Add items you need to bring to this event'
                    }
                    primaryAction={packingListLocked ? undefined : { label: 'Add Item', onClick: () => setAddDialogOpen(true) }}
                    secondaryAction={packingListLocked ? undefined : { label: 'Copy from Event', onClick: () => setCopyDialogOpen(true) }}
                />
            ) : (
                <>
                    {/* Insufficient Inventory Summary */}
                    {insufficientItems.length > 0 && (
                        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                            {insufficientItems.length} {insufficientItems.length === 1 ? 'item has' : 'items have'} insufficient inventory
                        </Alert>
                    )}
                    {/* Trailers Subsection */}
                    {trailers.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1, color: 'primary.main' }}>
                                Trailers for this event
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {trailerCount} {trailerCount === 1 ? 'trailer' : 'trailers'}
                            </Typography>
                            <Paper
                                sx={{
                                    overflowX: 'auto',
                                    width: '100%',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    bgcolor: 'action.hover',
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Photo</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Required</TableCell>
                                            <TableCell>Available</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {trailers.map(item => (
                                            <PackingListItemRow
                                                key={item.id}
                                                packingListItem={item}
                                                eventId={eventId}
                                                allItemLocations={allItemLocations}
                                                disabled={packingListLocked}
                                                disabledReason={packingListLocked ? PACKING_LIST_LOCK_MESSAGE : undefined}
                                                onRemove={() => {}}
                                                onPackingListChanged={() => {}}
                                                onRecordLostOrBroken={handleRecordLostOrBroken}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    )}

                    {/* Other Items Subsection */}
                    {otherItems.length > 0 && (
                        <Box>
                            {trailers.length > 0 && (
                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                    Other Items
                                </Typography>
                            )}
                            <Paper
                                sx={{
                                    overflowX: 'auto',
                                    width: '100%',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Photo</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Required</TableCell>
                                            <TableCell>Available</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {otherItems.map(item => (
                                            <PackingListItemRow
                                                key={item.id}
                                                packingListItem={item}
                                                eventId={eventId}
                                                allItemLocations={allItemLocations}
                                                disabled={packingListLocked}
                                                disabledReason={packingListLocked ? PACKING_LIST_LOCK_MESSAGE : undefined}
                                                onRemove={() => {}}
                                                onPackingListChanged={() => {}}
                                                onRecordLostOrBroken={handleRecordLostOrBroken}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    )}
                </>
            )}

            {/* Record Inventory Change Dialog */}
            <RecordInventoryChangeDialog
                open={recordDialogOpen}
                onClose={handleRecordDialogClose}
                itemType={itemForRecord}
                mode={recordDialogMode}
                event={eventId ? { id: eventId, name: data?.events?.[0]?.name || 'Event' } : null}
            />
        </Box>
    );
}
